"use client"

import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ChevronRight, HelpCircle } from "lucide-react"
import { IPhoneFrame } from "@/components/ui/iphone-frame"

interface FeatureAction {
  label: string
  onClick: () => void
  variant?: 'default' | 'outline' | 'ghost' | 'secondary'
}

interface Feature {
  step: string
  title?: string
  content: string
  image: string
  /** Optional image to show after pointer animation completes */
  imageAfterTap?: string
  /** Optional pointer position as [x%, y%] from top-left */
  pointerTarget?: [number, number]
  /** Optional custom actions for this step */
  actions?: FeatureAction[]
}

interface FeatureStepsProps {
  features: Feature[]
  className?: string
  title?: string
  imageHeight?: string
  onComplete?: () => void
  completeButtonLabel?: string
}

export function FeatureSteps({
  features,
  className,
  title = "How to get Started",
  imageHeight = "h-[400px]",
  onComplete,
  completeButtonLabel = "Done",
}: FeatureStepsProps) {
  const [currentFeature, setCurrentFeature] = useState(0)
  const [showPointer, setShowPointer] = useState(false)
  const [showAfterImage, setShowAfterImage] = useState(false)

  // Reset to step 0 when features array changes (e.g., switching between flows)
  useEffect(() => {
    setCurrentFeature(0)
    setShowPointer(false)
    setShowAfterImage(false)
  }, [features])

  const currentPointer = features[currentFeature]?.pointerTarget
  const currentAfterImage = features[currentFeature]?.imageAfterTap

  // Full animation cycle: hand in → tap → hand out → reset
  useEffect(() => {
    if (!currentPointer) return
    
    // Reset state at start
    setShowPointer(false)
    setShowAfterImage(false)
    
    // Timeline for one cycle:
    // 0ms: Start - hand starts moving in
    // 300ms: Hand visible, animating to target
    // 1200ms: Hand at target, "tap" happens
    // 1500ms: Image changes to after state
    // 2500ms: Hand starts moving away (hide pointer)
    // 3500ms: Reset to beginning state
    // 4000ms: Start next cycle
    
    const timers: NodeJS.Timeout[] = []
    
    const runCycle = () => {
      // Hand moves in
      setShowPointer(true)
      
      // Image changes after tap
      if (currentAfterImage) {
        timers.push(setTimeout(() => setShowAfterImage(true), 1200))
      }
      
      // Hand moves away
      timers.push(setTimeout(() => setShowPointer(false), 2500))
      
      // Reset image back
      if (currentAfterImage) {
        timers.push(setTimeout(() => setShowAfterImage(false), 3500))
      }
    }
    
    // Initial delay before first cycle
    timers.push(setTimeout(runCycle, 300))
    
    // Set up repeating cycle
    const cycleInterval = setInterval(() => {
      runCycle()
    }, 4500)
    
    return () => {
      timers.forEach(t => clearTimeout(t))
      clearInterval(cycleInterval)
    }
  }, [currentFeature, currentPointer, currentAfterImage])

  const handleNext = () => {
    if (currentFeature === features.length - 1) {
      onComplete?.()
    } else {
      setCurrentFeature((prev) => prev + 1)
    }
  }

  const handleStepClick = (index: number) => {
    setCurrentFeature(index)
  }

  const isLastStep = currentFeature === features.length - 1

  return (
    <div className={cn("p-3 md:p-4", className)}>
      <div className="max-w-6xl mx-auto w-full">
        <h2 className="text-lg md:text-xl font-bold text-center mb-4 text-foreground">
          {title}
        </h2>

        <div className="flex flex-col md:grid md:grid-cols-[1fr,auto] gap-4 md:gap-6 items-start">
          {/* Steps list */}
          <div className="order-2 md:order-1 space-y-2 md:pr-4">
            {features.map((feature, index) => (
                <motion.div
                key={index}
                className={cn(
                  "flex items-start gap-2.5 cursor-pointer p-2 rounded-lg transition-all duration-200",
                  index === currentFeature 
                    ? "bg-primary/5 border border-primary/20 shadow-sm" 
                    : "hover:bg-muted/50 border border-transparent"
                )}
                initial={{ opacity: 0.6 }}
                animate={{ opacity: index === currentFeature ? 1 : 0.6 }}
                onClick={() => handleStepClick(index)}
              >
                <motion.div
                  className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold transition-all duration-200",
                    index < currentFeature
                      ? "bg-primary text-primary-foreground"
                      : index === currentFeature
                        ? "bg-primary text-primary-foreground ring-2 ring-primary/20"
                        : "bg-muted text-muted-foreground"
                  )}
                >
                  {index < currentFeature ? (
                    <span>✓</span>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </motion.div>

                <div className="flex-1 min-w-0">
                  <h3
                    className={cn(
                      "font-medium text-sm mb-0.5 transition-colors",
                      index === currentFeature ? "text-primary" : "text-foreground"
                    )}
                  >
                    {feature.title || feature.step}
                  </h3>

                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {feature.content}
                  </p>

                  {/* Action buttons for current step */}
                  {index === currentFeature && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {feature.actions?.map((action, actionIndex) => (
                        <Button
                          key={actionIndex}
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            action.onClick()
                          }}
                          className="gap-1.5 text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200"
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                          {action.label}
                        </Button>
                      ))}
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation()
                          handleNext()
                        }}
                        size="sm"
                        className="gap-1"
                      >
                        {isLastStep ? completeButtonLabel : "Next"}
                        {!isLastStep && <ChevronRight className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Image display with iPhone frame */}
          <div
            className={cn(
              "order-1 md:order-2 flex items-center justify-center md:sticky md:top-4",
              imageHeight
            )}
          >
            <AnimatePresence mode="wait">
              {features.map(
                (feature, index) =>
                  index === currentFeature && (
                    <motion.div
                      key={index}
                      className="relative"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                    >
                      <IPhoneFrame>
                        <div className="relative">
                          {/* Before image */}
                          <motion.img
                            src={feature.image}
                            alt={feature.title || feature.step}
                            className="w-[180px] md:w-[200px] h-auto"
                            animate={{ opacity: showAfterImage && feature.imageAfterTap ? 0 : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          
                          {/* After tap image - overlaid on top */}
                          {feature.imageAfterTap && (
                            <motion.img
                              src={feature.imageAfterTap}
                              alt={`${feature.title || feature.step} - result`}
                              className="w-[180px] md:w-[200px] h-auto absolute inset-0"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: showAfterImage ? 1 : 0 }}
                              transition={{ duration: 0.3 }}
                            />
                          )}
                          
                          {/* Animated pointer overlay - inside the phone frame */}
                          {feature.pointerTarget && showPointer && (
                            <motion.div
                              className="absolute z-20 pointer-events-none"
                              initial={{ 
                                right: "5%",
                                bottom: "40%",
                                opacity: 0,
                              }}
                              animate={{
                                left: `${feature.pointerTarget[0]}%`,
                                top: `${feature.pointerTarget[1]}%`,
                                right: "auto",
                                bottom: "auto",
                                opacity: 1,
                              }}
                              transition={{
                                duration: 1,
                                ease: [0.25, 0.1, 0.25, 1],
                              }}
                            >
                              {/* Hand pointing finger */}
                              <svg 
                                width="40" 
                                height="40" 
                                viewBox="0 0 64 64" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                className="drop-shadow-lg"
                                style={{ transform: "rotate(-30deg) scaleX(-1)" }}
                              >
                                <path 
                                  d="M32 8C30 8 28.5 9.5 28.5 11.5V28L26 26C24.5 24.5 22 24.5 20.5 26C19 27.5 19 30 20.5 31.5L30 41C32 43 34.5 44 37 44H42C47.5 44 52 39.5 52 34V24C52 22 50.5 20.5 48.5 20.5C47.5 20.5 46.5 21 46 21.5V19C46 17 44.5 15.5 42.5 15.5C41.5 15.5 40.5 16 40 16.5V14C40 12 38.5 10.5 36.5 10.5C35.5 10.5 34.5 11 34 11.5V11.5C34 9.5 32.5 8 32 8Z" 
                                  fill="#FDBF60"
                                  stroke="#E8A33C"
                                  strokeWidth="1.5"
                                />
                                {/* Pulsing tap indicator */}
                                <motion.circle
                                  cx="32"
                                  cy="6"
                                  r="5"
                                  fill="hsl(var(--primary))"
                                  animate={{
                                    scale: [1, 1.8, 1],
                                    opacity: [0.7, 0.2, 0.7]
                                  }}
                                  transition={{
                                    duration: 1.2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                  }}
                                />
                              </svg>
                            </motion.div>
                          )}
                        </div>
                      </IPhoneFrame>
                    </motion.div>
                  )
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
